import { useCallback, useEffect, useMemo } from 'react';

import { fillInputValue } from '../utils/autofillFields';

interface UseLinkedInImportOptions {
  providerUrl?: string;
}

// The callback is served from Scout's host, which is not the page origin when the job
// ad is viewed on a client vanity domain, so the popup's own origin is what to trust.
const resolveCallbackOrigin = (providerUrl?: string): string | undefined => {
  if (!providerUrl) {
    return undefined;
  }

  try {
    return new URL(providerUrl, window.location.href).origin;
  } catch {
    return undefined;
  }
};

export const useLinkedInImport = ({ providerUrl }: UseLinkedInImportOptions): (() => void) => {
  const callbackOrigin = useMemo(() => resolveCallbackOrigin(providerUrl), [providerUrl]);

  useEffect(() => {
    if (!callbackOrigin) {
      return undefined;
    }

    const handleLinkedInMessage = (event: MessageEvent) => {
      if (event.origin !== callbackOrigin) {
        return;
      }

      const response = event.data?.response;
      if (!response) {
        return;
      }

      fillInputValue('application_candidate_attributes_firstname', response.first_name);
      fillInputValue('application_candidate_attributes_lastname', response.last_name);
      fillInputValue('application_candidate_attributes_email', response.email);
    };

    window.addEventListener('message', handleLinkedInMessage);
    return () => {
      window.removeEventListener('message', handleLinkedInMessage);
    };
  }, [callbackOrigin]);

  return useCallback(() => {
    if (!providerUrl || !callbackOrigin) {
      return;
    }

    const width = 880;
    const height = 600;
    const left = Number(window.screen.width / 2 - width / 2);
    const top = Number(window.screen.height / 2 - height / 2);
    const popup = window.open(
      providerUrl,
      'linkedin-api',
      `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=1, copyhistory=no, width=${width}, height=${height}, top=${top}, left=${left}`
    );

    popup?.postMessage('new import window', callbackOrigin);
  }, [providerUrl, callbackOrigin]);
};
